/**
 * Port of `src/app/access-request-pending/page.tsx`.
 *
 * Public route (`middleware.ts` lets it through with no session), so the SPA
 * must serve it too or a link in an approval email would bounce a signed-out
 * visitor to `/login` — a page becoming newly unreachable, which SAFE-04
 * forbids. Markup and every `pickView` branch are copied verbatim; only the
 * `searchParams` read changed.
 *
 * Note this page belongs to the Auth.js GOOGLE approval flow, which the SPA's
 * token login does not participate in. It is here for reachability, not
 * because the SPA drives it.
 */
import Link from "@/compat/link";
import { useSearchParams } from "@/compat/navigation";
import { Wallet, Mail, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

interface SearchParams {
  email?: string;
  status?: string;
  state?: string;
  admin?: string;
  action?: string;
  previous?: string;
  message?: string;
}

export function AccessRequestPendingPage() {
  // The Server Component awaited a `searchParams` promise; the SPA reads the
  // same keys off the live query string. `pickView` below is copied unchanged.
  const query = useSearchParams();
  const params: SearchParams = {
    email: query.get("email") ?? undefined,
    status: query.get("status") ?? undefined,
    state: query.get("state") ?? undefined,
    admin: query.get("admin") ?? undefined,
    action: query.get("action") ?? undefined,
    previous: query.get("previous") ?? undefined,
    message: query.get("message") ?? undefined,
  };
  const view = pickView(params);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--cp-canvas)] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--cp-accent)] text-white">
            <Wallet className="h-5 w-5" />
          </div>
          <span className="text-xl font-bold text-[var(--cp-ink)]">AIM</span>
        </div>

        <div className="cp-card rounded-[13px] p-8">
          <div
            className={`mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full ${view.iconWrap}`}
          >
            <view.Icon className="h-7 w-7" />
          </div>

          <h1 className="mb-2 text-center text-xl font-semibold text-[var(--cp-ink)]">
            {view.title}
          </h1>
          <p className="text-center text-sm text-[var(--cp-ink-2)]">{view.body}</p>

          {view.email && (
            <div className="mt-5 rounded-lg bg-[var(--cp-surface-2)] px-4 py-3 text-center text-sm font-medium text-[var(--cp-ink-2)]">
              {view.email}
            </div>
          )}

          <div className="mt-8 flex justify-center">
            <Link
              href={view.cta.href}
              className="rounded-full bg-[var(--cp-accent)] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-110"
            >
              {view.cta.label}
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[var(--cp-ink-3)]">
          Need help? Contact Pritesh directly.
        </p>
      </div>
    </div>
  );
}

interface View {
  Icon: React.ComponentType<{ className?: string }>;
  iconWrap: string;
  title: string;
  body: string;
  email?: string;
  cta: { href: string; label: string };
}

function pickView(p: SearchParams): View {
  const isAdmin = p.admin === "1";

  if (isAdmin && p.state === "ok" && p.action === "approved") {
    return {
      Icon: CheckCircle2,
      iconWrap: "bg-[var(--cp-emerald-wash)] text-[var(--cp-emerald)]",
      title: "Access approved",
      body: "We've created their AIM account and emailed them so they can sign in.",
      email: p.email,
      cta: { href: "/admin/access-requests", label: "Open access requests" },
    };
  }
  if (isAdmin && p.state === "ok" && p.action === "denied") {
    return {
      Icon: XCircle,
      iconWrap: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
      title: "Access denied",
      body: "The request has been marked denied and the requester has been notified.",
      email: p.email,
      cta: { href: "/admin/access-requests", label: "Open access requests" },
    };
  }
  if (isAdmin && p.state === "already") {
    const prev = p.previous === "APPROVED" ? "approved" : "denied";
    return {
      Icon: AlertTriangle,
      iconWrap: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
      title: "Already reviewed",
      body: `This request was already ${prev} earlier. No change made.`,
      cta: { href: "/admin/access-requests", label: "Open access requests" },
    };
  }
  if (isAdmin && p.state === "error") {
    return {
      Icon: AlertTriangle,
      iconWrap: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
      title: "Link is invalid",
      body: p.message || "This approval link is invalid or expired. Open the admin page to handle the request manually.",
      cta: { href: "/admin/access-requests", label: "Open access requests" },
    };
  }

  // Requester landing — Google login but their email is unknown to AIM.
  if (p.status === "disabled") {
    return {
      Icon: XCircle,
      iconWrap: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
      title: "Account disabled",
      body: "Your account exists but is currently disabled. Please contact Pritesh.",
      email: p.email,
      cta: { href: "/login", label: "Back to sign in" },
    };
  }
  return {
    Icon: Mail,
    iconWrap: "bg-[var(--cp-emerald-wash)] text-[var(--cp-emerald)]",
    title: "Request sent",
    body: "We've notified Pritesh. Once approved, you'll receive an email and can sign in with the same Google account.",
    email: p.email,
    cta: { href: "/login", label: "Back to sign in" },
  };
}
